import { useState } from "react";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ChevronRight } from "lucide-react";
import { pricingApi } from "@/services/pricing";
import { partnersApi } from "@/services/b2b";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { useCan } from "@/auth/permissions";
import { PricingSubnav } from "./PricingSubnav";
import { IntercityRoutesCard } from "./IntercityRoutesCard";

const INP = "h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px] focus:border-blue-500 focus:outline-none";
const usd = (c?: number | null) => (c == null ? "—" : `$${Math.round(c / 100 / 1000).toLocaleString()}k`);
const BLANK = { name: "", date_start: "", date_end: "", uplift_pct: "10" };

/** 查询失败态 + 重试（杜绝失败伪装成空表）。 */
function CardError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center gap-2 py-6 text-[13px] text-slate-500">
      加载失败 · <button onClick={onRetry} className="font-medium text-blue-600 hover:underline">重试</button>
    </div>
  );
}

/** 售价管理中心 · 售价配置（/admin/pricing/settings · canvas page-64）。 */
export function SellPricingSettingsPage() {
  const qc = useQueryClient();
  const canEdit = useCan("pricing.holidays.manage");
  const holQ = useQuery({ queryKey: ["pricing", "holidays"], queryFn: () => pricingApi.listHolidays() });
  const tierQ = useQuery({ queryKey: ["b2b", "tiers"], queryFn: () => partnersApi.listTiers() });
  const logQ = useQuery({ queryKey: ["pricing", "changes", "recent"], queryFn: () => pricingApi.listChanges() });
  const [f, setF] = useState({ ...BLANK });

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const create = useMutation({
    mutationFn: async () => {
      const r = await askReason({
        scene: "price_change", title: "新增节假日加价", subtitle: f.name,
        changes: [{ label: `${f.date_start} ~ ${f.date_end}`, before: "无加价", after: `${Number(f.uplift_pct) > 0 ? "+" : ""}${Number(f.uplift_pct)}%` }], confirmText: "确认并保存",
      });
      if (!r) throw new ReasonCancelled();
      return pricingApi.createHoliday({ name: f.name, date_start: f.date_start, date_end: f.date_end, uplift_pct: Number(f.uplift_pct), ...r });
    },
    onSuccess: () => { setF({ ...BLANK }); qc.invalidateQueries({ queryKey: ["pricing", "holidays"] }); },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "保存失败"); },
  });
  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await askReason({ scene: "price_change", required: false, title: "删除节假日加价", subtitle: holQ.data?.find((h) => h.id === id)?.name, danger: true, confirmText: "确认删除" });
      if (!r) throw new ReasonCancelled();
      return pricingApi.deleteHoliday(id, r);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing", "holidays"] }),
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "删除失败"); },
  });

  return (
    <PageShell title="售价配置" subtitle="节假日系数 · 城际用车价目 · 客户分级 · 改价日志">
      <PricingSubnav />
      <div className="text-[12px] text-slate-400">售价管理中心 › 售价配置</div>

      {/* 节假日系数 */}
      <Card title="节假日系数" action={canEdit ? <span className="text-[11px] text-slate-400">下方表单 + 添加区间</span> : undefined}>
        {canEdit && (
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-5">
            <label className="flex flex-col gap-1 md:col-span-2"><span className="text-[11px] font-medium text-slate-600">时段名称</span><input className={INP} placeholder="如 暑期" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
            <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-slate-600">起</span><input type="date" className={INP} value={f.date_start} onChange={(e) => setF({ ...f, date_start: e.target.value })} /></label>
            <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-slate-600">止</span><input type="date" className={INP} value={f.date_end} onChange={(e) => setF({ ...f, date_end: e.target.value })} /></label>
            <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-slate-600">加价 %</span>
              <div className="flex gap-2">
                <input className={INP} value={f.uplift_pct} onChange={(e) => setF({ ...f, uplift_pct: e.target.value })} />
                <button disabled={create.isPending || !f.name || !f.date_start || !f.date_end} onClick={() => create.mutate()} className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> 添加区间</button>
              </div>
            </label>
          </div>
        )}
        {holQ.isError ? <CardError onRetry={() => holQ.refetch()} /> : holQ.isLoading ? <LoadingState /> : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3 text-left">时段</th><th className="px-4 py-3 text-left">日期范围</th><th className="px-4 py-3 text-left">系数</th><th className="px-4 py-3 text-left">适用</th>{canEdit && <th className="px-4 py-3 text-right">操作</th>}</tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-800">平季</td>
                  <td className="px-4 py-3 text-slate-500">默认</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">× 1.00</td>
                  <td className="px-4 py-3 text-slate-600">全产品</td>
                  {canEdit && <td />}
                </tr>
                {holQ.data?.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800">{h.name}</td>
                    <td className="px-4 py-3 text-slate-600">{h.date_start} → {h.date_end}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">× {(1 + h.uplift_pct / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-slate-600">全产品</td>
                    {canEdit && <td className="px-4 py-3 text-right"><button onClick={() => { if (confirm(`删除「${h.name}」？`)) del.mutate(h.id); }} className="rounded-md border border-rose-300 p-1.5 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2 text-[11px] text-slate-400">酒店实际涨价另自动传导到成本（双轨：成本自动 + 手动旺季系数）。</div>
      </Card>

      {/* INTERCITY-1 · 城际用车价目（财务维护） */}
      <IntercityRoutesCard />

      {/* 客户分级 */}
      <Card title="客户分级" action={<Link to="/admin/pricing/tiers" className="flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700">管理分级 <ChevronRight className="h-3.5 w-3.5" /></Link>}>
        {tierQ.isError ? <CardError onRetry={() => tierQ.refetch()} /> : tierQ.isLoading ? <LoadingState /> : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[640px] text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3 text-left">级别</th><th className="px-4 py-3 text-left">自动定级</th><th className="px-4 py-3 text-left">markup</th><th className="px-4 py-3 text-left">账期</th><th className="px-4 py-3 text-left">信用区间</th></tr>
              </thead>
              <tbody>
                {tierQ.data?.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800">{t.name}</td>
                    <td className="px-4 py-3 text-slate-600">月单 ≥ {t.min_monthly_orders}{t.min_annual_gmv_cents ? ` 或 年流水 ≥ ${usd(t.min_annual_gmv_cents)}` : ""}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{t.baseline_markup_pct > 0 ? "+" : ""}{t.baseline_markup_pct}%</td>
                    <td className="px-4 py-3 text-slate-600">{t.settlement_terms}</td>
                    <td className="px-4 py-3 text-slate-600">{usd(t.credit_min_cents)} – {usd(t.credit_max_cents)}</td>
                  </tr>
                ))}
                {tierQ.data?.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">暂无分级</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 改价日志 */}
      <Card title="改价日志" action={<Link to="/admin/pricing/change-requests" className="flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700">全部日志 <ChevronRight className="h-3.5 w-3.5" /></Link>}>
        {logQ.isError ? <CardError onRetry={() => logQ.refetch()} /> : logQ.isLoading ? <LoadingState /> : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[680px] text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3 text-left">时间</th><th className="px-4 py-3 text-left">操作人</th><th className="px-4 py-3 text-left">对象</th><th className="px-4 py-3 text-left">改动</th><th className="px-4 py-3 text-left">原因</th></tr>
              </thead>
              <tbody>
                {logQ.data?.slice(0, 10).map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-500">{new Date(c.submitted_at).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-3 text-slate-700">{c.submitted_by}</td>
                    <td className="px-4 py-3 text-slate-700">{c.target_ref}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.current_pct}% → {c.proposed_pct}%</td>
                    <td className="px-4 py-3 text-slate-600">{c.reason}</td>
                  </tr>
                ))}
                {logQ.data?.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">暂无改价记录</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    {reasonDialog}
    </PageShell>
  );
}
